// Named deep-sky objects (DSOs) at correct celestial coordinates.
// Adds the diffuse fuzzy patches the star catalogue cannot represent
// — galaxies, emission nebulae, reflection nebulae, and Magellanic
// Clouds — as billboard sprites on the same celestial shell as the
// star catalogue.
//
// Andromeda alone is a huge realism gain because it's the brightest
// extragalactic object visible to the naked eye and unmistakably
// absent without it.
//
// Each DSO is rendered by a custom ShaderMaterial driven from a
// per-DSO config (type, axial ratio, core/halo colours, structure
// frequency). The shader produces:
//   galaxy     — tilted ellipse with bright bulge, exponential disc
//                falloff, dust-lane darkening, fBm structure overlay
//   emission   — irregular pink-red with bright core, multi-octave
//                noise; suggests the wisps + filaments of M42-class
//                Hα regions
//   reflection — smooth cool-blue glow with low-frequency noise;
//                M45 / Pleiades-style scattered dust
//
// If you ever want true photoreal, drop a public-domain image (NASA
// / ESO) into public/textures/ named e.g. m31.jpg and pass
// `texture: loadedTexture` in the catalogue entry; the shader picks
// it up via the photoTex uniform and skips the procedural path.

import * as THREE from 'three';
import { raDecToScene, CELESTIAL_NORTH } from './sky/frame.js';
import { OKLAB_MIX_GLSL } from './shaders/oklab.js';

// Deep-sky object catalogue. Magnitudes / sizes / colours sourced
// from SEDS, NED, and SIMBAD. RA in decimal hours, Dec in decimal
// degrees. widthDeg/heightDeg are the apparent angular footprint
// you'd actually see in a wide-field astrophotograph (typically
// somewhat larger than the naked-eye visible region — but we're
// matching the photo aesthetic).
const DSO_CATALOG = [
  {
    name: 'M31 Andromeda',
    raHours: 0.7124,
    decDeg: 41.269,
    // Adam Evans full-disc photo (1280×841, ~1.52:1). Sized larger
    // than M31's true ~3° angular extent so the galaxy reads
    // clearly at default FOV. Height is overridden at runtime from
    // the photo's actual aspect.
    widthDeg: 5.0,
    heightDeg: 3.3,
    type: 'galaxy',
    tiltDeg: 35,
    coreColor:  [1.00, 0.94, 0.82],
    armColor:   [0.95, 0.85, 0.70],
    haloColor:  [0.55, 0.45, 0.35],
    intensity:  1.4,
    dustLane:   0.55
  },
  {
    name: 'M42 Orion Nebula',
    raHours: 5.5881,
    decDeg: -5.391,
    widthDeg: 1.1,
    heightDeg: 1.0,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [1.00, 0.78, 0.86],
    armColor:   [0.95, 0.55, 0.65],
    haloColor:  [0.45, 0.25, 0.40],
    intensity:  1.2,
    dustLane:   0.0
  },
  {
    name: 'M45 Pleiades',
    raHours: 3.7833,
    decDeg: 24.117,
    widthDeg: 2.0,
    heightDeg: 2.0,
    type: 'reflection',
    tiltDeg: 0,
    coreColor:  [0.85, 0.92, 1.00],
    armColor:   [0.55, 0.72, 1.00],
    haloColor:  [0.20, 0.30, 0.55],
    intensity:  0.65,
    dustLane:   0.0
  },
  {
    name: 'M8 Lagoon Nebula',
    raHours: 18.058,
    decDeg: -24.383,
    widthDeg: 1.5,
    heightDeg: 0.8,
    type: 'emission',
    tiltDeg: 15,
    coreColor:  [1.00, 0.70, 0.72],
    armColor:   [0.85, 0.45, 0.50],
    haloColor:  [0.40, 0.18, 0.22],
    intensity:  0.85,
    dustLane:   0.25
  },
  {
    name: 'M16 Eagle Nebula',
    raHours: 18.310,
    decDeg: -13.783,
    widthDeg: 0.8,
    heightDeg: 0.7,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [1.00, 0.78, 0.82],
    armColor:   [0.85, 0.50, 0.62],
    haloColor:  [0.40, 0.20, 0.30],
    intensity:  0.70,
    dustLane:   0.0
  },
  {
    name: 'M20 Trifid Nebula',
    raHours: 18.039,
    decDeg: -23.033,
    widthDeg: 0.5,
    heightDeg: 0.5,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.95, 0.70, 0.95],
    armColor:   [0.70, 0.45, 0.85],
    haloColor:  [0.30, 0.18, 0.45],
    intensity:  0.60,
    dustLane:   0.0
  },
  {
    name: 'LMC',
    raHours: 5.392,
    decDeg: -69.756,
    widthDeg: 5.5,
    heightDeg: 5.0,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.94, 0.85],
    armColor:   [0.92, 0.82, 0.72],
    haloColor:  [0.45, 0.36, 0.30],
    intensity:  1.2,
    dustLane:   0.10
  },
  {
    name: 'SMC',
    raHours: 0.867,
    decDeg: -72.833,
    widthDeg: 3.5,
    heightDeg: 3.0,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.94, 0.85],
    armColor:   [0.90, 0.80, 0.70],
    haloColor:  [0.40, 0.32, 0.25],
    intensity:  0.95,
    dustLane:   0.05
  },
  {
    name: 'M104 Sombrero',
    raHours: 12.667,
    decDeg: -11.62,
    widthDeg: 1.4,
    heightDeg: 0.79,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.95, 0.82],
    armColor:   [0.92, 0.82, 0.68],
    haloColor:  [0.45, 0.40, 0.35],
    intensity:  1.30,
    dustLane:   0.60
  },
  {
    name: 'NGC 7293 Helix',
    raHours: 22.493,
    decDeg: -20.84,
    widthDeg: 1.6,
    heightDeg: 1.72,
    // Eye-of-God planetary nebula. Emission-class shader gives it
    // the right multi-octave structure look.
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.80, 0.95, 1.00],
    armColor:   [0.95, 0.55, 0.55],
    haloColor:  [0.35, 0.25, 0.40],
    intensity:  1.20,
    dustLane:   0.0
  },
  {
    name: 'M1 Crab Nebula',
    raHours: 5.575,
    decDeg: 22.014,
    widthDeg: 0.85,
    heightDeg: 0.85,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.85, 0.85, 1.00],
    armColor:   [1.00, 0.62, 0.55],
    haloColor:  [0.30, 0.28, 0.45],
    intensity:  1.10,
    dustLane:   0.0
  },
  {
    name: 'M51 Whirlpool',
    raHours: 13.498,
    decDeg: 47.196,
    widthDeg: 1.6,
    heightDeg: 1.11,
    type: 'galaxy',
    tiltDeg: 25,
    coreColor:  [1.00, 0.94, 0.85],
    armColor:   [0.95, 0.85, 0.95],
    haloColor:  [0.50, 0.45, 0.55],
    intensity:  1.30,
    dustLane:   0.40
  },
  {
    name: 'M57 Ring Nebula',
    raHours: 18.893,
    decDeg: 33.029,
    widthDeg: 0.55,
    heightDeg: 0.56,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.85, 1.00, 0.85],
    armColor:   [1.00, 0.55, 0.55],
    haloColor:  [0.30, 0.30, 0.30],
    intensity:  1.00,
    dustLane:   0.0
  },
  {
    name: 'Horsehead Nebula',
    raHours: 5.683,
    decDeg: -2.45,
    widthDeg: 1.1,
    heightDeg: 1.15,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [1.00, 0.80, 0.85],
    armColor:   [0.95, 0.60, 0.65],
    haloColor:  [0.30, 0.20, 0.30],
    intensity:  1.20,
    dustLane:   0.30
  },
  {
    name: 'Veil Nebula',
    raHours: 20.85,
    decDeg: 30.72,
    widthDeg: 1.6,
    heightDeg: 1.07,
    type: 'emission',
    tiltDeg: 30,
    coreColor:  [0.85, 1.00, 0.95],
    armColor:   [1.00, 0.55, 0.65],
    haloColor:  [0.25, 0.35, 0.40],
    intensity:  1.05,
    dustLane:   0.0
  },
  {
    // JWST NIRCam "Cosmic Cliffs" image of NGC 3324 in the Carina
    // Nebula. Aspect ~4:3 (2048×1536); applyAspect at runtime
    // overrides heightDeg from the photo's real dimensions.
    name: 'Carina Nebula',
    raHours: 10.75,
    decDeg: -59.87,
    widthDeg: 2.4,
    heightDeg: 1.80,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [1.00, 0.78, 0.78],
    armColor:   [1.00, 0.55, 0.55],
    haloColor:  [0.40, 0.20, 0.30],
    intensity:  1.30,
    dustLane:   0.20
  },
  {
    name: 'NGC 6543 Cat\'s Eye',
    raHours: 17.97,
    decDeg: 66.63,
    widthDeg: 0.55,
    heightDeg: 0.55,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.85, 1.00, 0.95],
    armColor:   [0.65, 0.85, 1.00],
    haloColor:  [0.25, 0.35, 0.40],
    intensity:  1.05,
    dustLane:   0.0
  },
  {
    // Real M81 sits at Dec +69.07°, but its sprite (1.0° tall) heavily
    // overlapped M82 just 0.6° away. Nudged 0.4° south to give clean
    // visual separation between the famous galaxy pair.
    name: 'M81 Bode\'s Galaxy',
    raHours: 9.93,
    decDeg: 68.65,
    widthDeg: 1.2,
    heightDeg: 0.81,
    type: 'galaxy',
    tiltDeg: 30,
    coreColor:  [1.00, 0.94, 0.85],
    armColor:   [0.95, 0.85, 0.80],
    haloColor:  [0.50, 0.45, 0.45],
    intensity:  1.30,
    dustLane:   0.45
  },
  {
    // Real M82 sits at Dec +69.68° — nudged 0.5° north so the
    // M81/M82 pair reads as two distinct galaxies in the menu's
    // telescope view.
    name: 'M82 Cigar Galaxy',
    raHours: 9.93,
    decDeg: 70.20,
    widthDeg: 1.0,
    heightDeg: 0.69,
    type: 'galaxy',
    tiltDeg: 65,
    coreColor:  [1.00, 0.85, 0.75],
    armColor:   [1.00, 0.55, 0.45],
    haloColor:  [0.50, 0.40, 0.35],
    intensity:  1.50,
    dustLane:   0.50
  },
  {
    name: 'M33 Triangulum',
    raHours: 1.564,
    decDeg: 30.66,
    widthDeg: 2.4,
    heightDeg: 1.35,
    type: 'galaxy',
    tiltDeg: 20,
    coreColor:  [1.00, 0.94, 0.82],
    armColor:   [0.95, 0.85, 0.72],
    haloColor:  [0.50, 0.45, 0.40],
    intensity:  1.20,
    dustLane:   0.30
  },
  {
    name: 'M101 Pinwheel',
    raHours: 14.054,
    decDeg: 54.35,
    widthDeg: 1.6,
    heightDeg: 1.25,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.94, 0.82],
    armColor:   [0.95, 0.85, 0.85],
    haloColor:  [0.50, 0.45, 0.50],
    intensity:  1.30,
    dustLane:   0.30
  },
  {
    name: 'M27 Dumbbell',
    raHours: 19.994,
    decDeg: 22.72,
    widthDeg: 0.65,
    heightDeg: 0.81,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [0.85, 1.00, 0.85],
    armColor:   [1.00, 0.55, 0.55],
    haloColor:  [0.30, 0.40, 0.30],
    intensity:  1.05,
    dustLane:   0.0
  },
  {
    name: 'Centaurus A',
    raHours: 13.426,
    decDeg: -43.02,
    widthDeg: 1.5,
    heightDeg: 1.58,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.92, 0.78],
    armColor:   [0.85, 0.72, 0.62],
    haloColor:  [0.40, 0.30, 0.25],
    intensity:  1.30,
    dustLane:   0.65
  },
  {
    name: 'M87',
    raHours: 12.514,
    decDeg: 12.39,
    widthDeg: 0.85,
    heightDeg: 0.85,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.92, 0.75],
    armColor:   [0.85, 0.72, 0.55],
    haloColor:  [0.40, 0.32, 0.25],
    intensity:  1.20,
    dustLane:   0.0
  },
  {
    name: 'NGC 6302 Butterfly',
    raHours: 17.227,
    decDeg: -37.10,
    widthDeg: 0.55,
    heightDeg: 0.64,
    type: 'emission',
    tiltDeg: 25,
    coreColor:  [1.00, 0.85, 0.92],
    armColor:   [0.85, 0.45, 0.65],
    haloColor:  [0.30, 0.20, 0.30],
    intensity:  1.10,
    dustLane:   0.0
  },
  {
    name: 'Rosette Nebula',
    raHours: 6.516,
    decDeg: 4.95,
    widthDeg: 1.6,
    heightDeg: 1.21,
    type: 'emission',
    tiltDeg: 0,
    coreColor:  [1.00, 0.65, 0.62],
    armColor:   [0.95, 0.55, 0.55],
    haloColor:  [0.35, 0.20, 0.25],
    intensity:  1.10,
    dustLane:   0.0
  },
  {
    // Hubble Ultra Deep Field — the famous 2004 1-million-second
    // exposure of a tiny patch of sky in Fornax revealing ~10,000
    // galaxies stretching to the edge of the observable universe.
    name: 'Hubble Ultra Deep Field',
    raHours: 3.526,
    decDeg: -27.78,
    widthDeg: 0.85,
    heightDeg: 0.85,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.95, 0.85],
    armColor:   [0.85, 0.78, 0.70],
    haloColor:  [0.40, 0.36, 0.32],
    intensity:  1.30,
    dustLane:   0.0
  },
  {
    // Hubble Deep Field — the original 1995 deep exposure in Ursa
    // Major. The image that proved galaxies fill every patch of
    // apparently empty sky.
    name: 'Hubble Deep Field',
    raHours: 12.633,
    decDeg: 62.20,
    widthDeg: 0.7,
    heightDeg: 0.72,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.95, 0.85],
    armColor:   [0.85, 0.78, 0.70],
    haloColor:  [0.40, 0.36, 0.32],
    intensity:  1.20,
    dustLane:   0.0
  },
  {
    name: 'M64 Black Eye',
    raHours: 12.945,
    decDeg: 21.68,
    widthDeg: 0.85,
    heightDeg: 0.85,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.92, 0.78],
    armColor:   [0.85, 0.68, 0.55],
    haloColor:  [0.40, 0.30, 0.25],
    intensity:  1.25,
    dustLane:   0.70
  },
  {
    name: 'NGC 4565 Needle',
    raHours: 12.605,
    decDeg: 25.99,
    widthDeg: 1.4,
    heightDeg: 1.40,
    type: 'galaxy',
    tiltDeg: 75,
    coreColor:  [1.00, 0.92, 0.78],
    armColor:   [0.85, 0.72, 0.62],
    haloColor:  [0.40, 0.32, 0.28],
    intensity:  1.20,
    dustLane:   0.55
  },
  {
    name: 'M83 Southern Pinwheel',
    raHours: 13.617,
    decDeg: -29.87,
    widthDeg: 1.5,
    heightDeg: 0.97,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.92, 0.82],
    armColor:   [0.95, 0.75, 0.85],
    haloColor:  [0.45, 0.38, 0.42],
    intensity:  1.30,
    dustLane:   0.30
  },
  {
    name: 'Antennae Galaxies',
    raHours: 12.018,
    decDeg: -18.87,
    widthDeg: 1.0,
    heightDeg: 1.0,
    type: 'galaxy',
    tiltDeg: 30,
    coreColor:  [1.00, 0.92, 0.78],
    armColor:   [0.95, 0.62, 0.85],
    haloColor:  [0.40, 0.30, 0.40],
    intensity:  1.25,
    dustLane:   0.20
  },
  {
    name: 'NGC 1300 Barred Spiral',
    raHours: 3.327,
    decDeg: -19.41,
    widthDeg: 1.5,
    heightDeg: 0.86,
    type: 'galaxy',
    tiltDeg: 0,
    coreColor:  [1.00, 0.92, 0.78],
    armColor:   [0.95, 0.85, 0.75],
    haloColor:  [0.50, 0.42, 0.38],
    intensity:  1.25,
    dustLane:   0.30
  }
];

const TYPE_ID = { galaxy: 0, emission: 1, reflection: 2 };

// Billboard vertex shader with the celestial north locked.
//
// Standard sprite billboards align with the camera's screen axes —
// when the camera rolls, the sprite rolls with it. That's wrong for
// celestial objects: M31's disc has a fixed orientation in the sky,
// it shouldn't spin when you tilt your head. We want the quad to
// always face the camera (so it's never edge-on) BUT keep its
// up-axis locked to the north celestial pole (src/sky/frame.js) so it
// stays correctly oriented in the celestial frame regardless of camera
// roll, north up and east to the left as the photos are.
//
// Approach: compute the direction from sprite to camera in world
// space, then build a right/up basis where right = north × to_cam
// and up = to_cam × right. The quad lives in the plane spanned by
// those two world-space vectors, scaled by the sprite's modelMatrix
// scale (which encodes the desired angular size on the celestial
// shell).
const dsoVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;

    vec3 spriteWorldPos = vec3(modelMatrix[3].x, modelMatrix[3].y, modelMatrix[3].z);
    vec3 toCam = normalize(cameraPosition - spriteWorldPos);

    // The north celestial pole. Fallback for the degenerate case where
    // toCam is parallel to it (a sprite at a pole): bias slightly so the
    // cross product stays non-zero.
    vec3 worldUp = vec3(${CELESTIAL_NORTH.x.toFixed(6)}, ${CELESTIAL_NORTH.y.toFixed(6)}, ${CELESTIAL_NORTH.z.toFixed(6)});
    if (abs(dot(toCam, worldUp)) > 0.999) {
      worldUp = normalize(worldUp + vec3(0.0001, 0.0, 0.0));
    }
    vec3 right = normalize(cross(worldUp, toCam));
    vec3 up    = normalize(cross(toCam, right));

    vec2 scale;
    scale.x = length(vec3(modelMatrix[0].x, modelMatrix[0].y, modelMatrix[0].z));
    scale.y = length(vec3(modelMatrix[1].x, modelMatrix[1].y, modelMatrix[1].z));

    vec3 worldPos = spriteWorldPos
      + right * position.x * scale.x
      + up    * position.y * scale.y;

    gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
  }
`;

const dsoFragmentShader = /* glsl */`
  ${OKLAB_MIX_GLSL}
  varying vec2 vUv;
  uniform vec3 coreColor;
  uniform vec3 armColor;
  uniform vec3 haloColor;
  uniform float intensity;
  uniform float dustLane;
  uniform float tiltRad;
  uniform int dsoType;          // 0 galaxy, 1 emission, 2 reflection
  uniform sampler2D photoTex;
  uniform float usePhoto;       // 1.0 if photoTex is real
  uniform float photoAspect;    // sprite width / height; 1.0 if unknown
  // Visibility scales with how much the user has zoomed in (lower
  // FOV = larger). At default 45° FOV the DSO reads as a faint
  // nearly-invisible patch the way it would to the naked eye; once
  // the user focuses via the menu (FOV ≈ 14°) it climbs to clearly
  // visible; tight zoom (FOV ≈ 5°) shows the full photo brightness.
  uniform float zoomVisibility;

  // Hash + value-noise basis; cheap, no external buffer.
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * noise(p);
      p *= 2.07;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    // Photo path. The photo arrives cleaned for the sky by
    // scripts/build-dso-photos.mjs: its own sky floor and gradients
    // subtracted, the foreground stars of another camera taken out,
    // and its coverage baked into the colour, fading at the frame
    // along the image's own structure. Under additive blending only
    // the object's light is added, so there is nothing left to mask
    // here.
    if (usePhoto > 0.5) {
      vec3 col = texture2D(photoTex, vUv).rgb;
      gl_FragColor = vec4(col * intensity * zoomVisibility, 1.0);
      return;
    }

    // Procedural path. Centre-relative coords, then rotate by tilt
    // so the long axis of galaxies points the right way on the sky.
    vec2 c = vUv - 0.5;
    float ct = cos(tiltRad);
    float st = sin(tiltRad);
    c = vec2(ct * c.x - st * c.y, st * c.x + ct * c.y);

    // All procedural paths apply the same zoomVisibility multiplier
    // as the photo path. Without it
    // every unloaded DSO (33 of them under lazy loading) was rendering
    // at full procedural intensity at default zoom — producing huge
    // bright blotches across the celestial sphere.
    if (dsoType == 0) {
      float r = length(c) * 2.0;
      float bulge = exp(-r * 5.5);
      float disc  = exp(-r * 2.0) * 0.55;
      float arms = fbm(c * 12.0 + vec2(3.1, 7.7));
      float armBoost = smoothstep(0.45, 0.85, arms) * 0.45 * exp(-r * 2.5);
      float laneDist = abs(c.y) * 14.0;
      float lane = exp(-laneDist * laneDist) * smoothstep(0.6, 0.0, r) * dustLane;
      float halo = exp(-r * 1.1) * 0.18;

      vec3 col = coreColor * bulge
               + armColor  * (disc + armBoost)
               + haloColor * halo;
      col *= 1.0 - lane;

      float envelope = smoothstep(1.05, 0.0, r);
      float vis = envelope * (bulge + disc + halo);
      gl_FragColor = vec4(col * intensity * zoomVisibility, vis * zoomVisibility);
    } else if (dsoType == 1) {
      float r = length(c) * 2.0;
      float n  = fbm(c * 6.5 + vec2(1.7, 3.3));
      float n2 = fbm(c * 2.2 + vec2(7.1, 2.0));
      float structure = mix(n2, n, 0.5);
      float envelope = smoothstep(1.05, 0.05, r);
      float core = exp(-r * 7.0);
      float wisps = smoothstep(0.30, 0.85, structure) * envelope;

      vec3 col = coreColor * core * 1.4
               + armColor  * wisps
               + haloColor * envelope * 0.18;
      float vis = envelope * (wisps + core * 1.2);
      gl_FragColor = vec4(col * intensity * zoomVisibility, vis * zoomVisibility);
    } else {
      float r = length(c) * 2.0;
      float n = fbm(c * 3.5 + vec2(11.3, 5.1));
      float envelope = smoothstep(1.05, 0.05, r);
      float diffuse = (0.4 + n * 0.6) * envelope;

      // OKLCh perceptual mix for the reflection-nebula halo↔arm
      // gradient. Reflection nebulae span wide hue (blue dust scatter
      // over warm starlight, or vice versa); sRGB mix cuts through
      // grey, OKLCh rotates through the perceptual hue arc so the
      // transition stays chromatic. Galaxy/emission paths above use
      // additive accumulation, which is already physically correct
      // for emission — no OKLCh needed there.
      vec3 col = oklchMix(haloColor, armColor, diffuse) * envelope
               + coreColor * envelope * envelope * 0.35;
      gl_FragColor = vec4(col * intensity * zoomVisibility, diffuse * 0.7 * zoomVisibility);
    }
  }
`;

// Halves src down to tw x th, by at most half per step so each step averages
// the pixels it drops, and returns the last canvas (src itself if no step).
function halvePhoto(src, w, h, tw, th) {
  while (w !== tw || h !== th) {
    w = Math.max(tw, Math.ceil(w / 2)); h = Math.max(th, Math.ceil(h / 2));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(src, 0, 0, w, h);
    src = canvas;
  }
  return src;
}

function capSize(w0, h0, maxSize) {
  const scale = maxSize / Math.max(w0, h0);
  return [Math.max(1, Math.round(w0 * scale)), Math.max(1, Math.round(h0 * scale))];
}

// A loaded photo shrunk in place to maxSize on its long edge; the full decode
// goes with the <img>. Only where loadShrunkPhoto cannot run.
function shrinkPhoto(tex, maxSize) {
  const src = tex.image;
  const w0 = src && (src.naturalWidth || src.width), h0 = src && (src.naturalHeight || src.height);
  if (!w0 || !h0 || Math.max(w0, h0) <= maxSize || typeof document === 'undefined') return;
  try {
    tex.image = halvePhoto(src, w0, h0, ...capSize(w0, h0, maxSize));
  } catch (err) {
    console.warn('[dso] photo shrink failed; keeping full size', err);
  }
}

// The photo at url as a texture at most maxSize on its long edge, decoded
// from the file's bytes, which the browser does off the main thread. Drawn
// from an <img> (shrinkPhoto), the decode ran on the main thread: 60 to 100 ms
// a photo on a mid-range phone and 1.2 s for the 33 photos at load, which
// taps then waited behind. The decode is the same, and the halving from it
// comes out as close to a true average of the photo as from the <img> (the
// same mean error, measured on four photos, 2 Nov 2026).
async function loadShrunkPhoto(url, maxSize) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const bitmap = await createImageBitmap(await res.blob());
  try {
    const w0 = bitmap.width, h0 = bitmap.height;
    const [tw, th] = Math.max(w0, h0) > maxSize ? capSize(w0, h0, maxSize) : [w0, h0];
    // Always a canvas: the upload's flip does not apply to an ImageBitmap.
    let canvas = halvePhoto(bitmap, w0, h0, tw, th);
    if (canvas === bitmap) canvas = copyToCanvas(bitmap);
    const tex = new THREE.Texture(canvas);
    tex.needsUpdate = true;
    return tex;
  } finally {
    bitmap.close();
  }
}

function copyToCanvas(src) {
  const canvas = document.createElement('canvas');
  canvas.width = src.width; canvas.height = src.height;
  canvas.getContext('2d').drawImage(src, 0, 0);
  return canvas;
}

// Configures the photo path each sprite expects. Two modes:
//   1. `texturesByName` — the legacy eager map (name → already-loaded
//      Texture). If provided, each sprite gets its photo immediately.
//   2. `photoFiles` — name → photo filename (e.g. 'm31.webp'). The factory
//      stashes the filename on each sprite's userData so the consumer
//      (focusOnDSO in main.js) can lazy-load on first focus. Avoids
//      downloading 10MB+ of textures users may never look at.
export function createDeepSkyObjects(shellRadius, texturesByName = {}, options = {}) {
  // photoMaxSize / photoFocusSize: a phone's cap on a photo's long edge in the
  // sky and when focused (0 keeps the file's size); docs/mobile-memory.md.
  const { photoFiles = null, photoBaseUrl = '/textures/dso/', photoMaxSize = 0, photoFocusSize = 0 } = options;
  const group = new THREE.Group();
  group.name = 'deep-sky-objects';
  // Render right after the panorama / catalogue stars (-1000 / -999)
  // so DSOs composite on top of background but below solar bodies.
  group.renderOrder = -998;
  group.frustumCulled = false;

  const degToRad = Math.PI / 180;
  const angleToSize = (deg) => shellRadius * (deg * degToRad);

  // Reused texture loader for lazy-load mode so disposed textures
  // and HTTP cache work coherently.
  const loader = new THREE.TextureLoader();

  for (const dso of DSO_CATALOG) {
    const photo = texturesByName[dso.name] || null;
    if (photo) {
      // Photos benefit from anisotropic filtering so they stay sharp
      // when viewed at a glancing angle as the camera pans across the
      // celestial sphere. SRGBColorSpace so the color in the JPG
      // doesn't double-gamma against the renderer's tone-mapping.
      photo.colorSpace = THREE.SRGBColorSpace;
      photo.anisotropy = 4;
      // Mipmapped: in the panorama a 1280 px photo covers a hundred
      // or so screen pixels, and without mips the minified samples
      // sparkle as the view moves. WebGL2 mipmaps the non-power-of-two
      // photos; ClampToEdge keeps the black frame from wrapping.
      photo.wrapS = THREE.ClampToEdgeWrapping;
      photo.wrapT = THREE.ClampToEdgeWrapping;
      photo.minFilter = THREE.LinearMipmapLinearFilter;
      photo.magFilter = THREE.LinearFilter;
      photo.generateMipmaps = true;
    }

    const material = new THREE.ShaderMaterial({
      uniforms: {
        coreColor: { value: new THREE.Color().fromArray(dso.coreColor) },
        armColor:  { value: new THREE.Color().fromArray(dso.armColor) },
        haloColor: { value: new THREE.Color().fromArray(dso.haloColor) },
        intensity: { value: dso.intensity },
        dustLane:  { value: dso.dustLane },
        tiltRad:   { value: (dso.tiltDeg || 0) * degToRad },
        dsoType:   { value: TYPE_ID[dso.type] ?? 0 },
        photoTex:  { value: photo },
        usePhoto:  { value: photo ? 1.0 : 0.0 },
        // Set initially from catalog; overwritten once the photo's
        // image dimensions are known via applyAspect below.
        photoAspect: { value: dso.widthDeg / dso.heightDeg },
        // Driven from the animate() loop based on camera.fov. 1.0 =
        // full photo brightness; lower values fade the DSO toward
        // invisible at wide FOV.
        zoomVisibility: { value: 0.1 }
      },
      vertexShader: dsoVertexShader,
      fragmentShader: dsoFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      // Additive blending: physically right for translucent emitters.
      // Real nebulae and galaxies ADD their light to whatever is
      // behind them — you see background stars through M31's disc,
      // not in front of it. The cleaned photos are black wherever the
      // object is not, so additive only brightens where it lives.
      blending: THREE.AdditiveBlending
    });

    const sprite = new THREE.Sprite(material);
    sprite.position.copy(raDecToScene(dso.raHours, dso.decDeg)).multiplyScalar(shellRadius);

    // Default scale from catalog. If a photo is supplied we re-derive
    // height from its actual aspect ratio so the picture isn't
    // stretched. Photo dimensions come from the underlying HTML
    // image; if it hasn't loaded yet, we install a one-shot updater
    // that fires on Texture's image-update event.
    sprite.scale.set(angleToSize(dso.widthDeg), angleToSize(dso.heightDeg), 1);
    if (photo) {
      const applyAspect = () => {
        const img = photo.image;
        if (!img || !img.width) return;
        const aspect = img.width / img.height;
        const heightDeg = dso.widthDeg / aspect;
        sprite.scale.set(angleToSize(dso.widthDeg), angleToSize(heightDeg), 1);
        // Sync the shader's vignette to the actual photo aspect so
        // panoramic images (M31 in particular) keep their long-axis
        // content visible.
        material.uniforms.photoAspect.value = aspect;
      };
      if (photo.image && photo.image.width) {
        applyAspect();
      } else {
        // TextureLoader sets `image` after the JPEG decodes. We poll
        // a couple of ticks rather than wiring a full event handler;
        // it's a one-time check at boot.
        const start = performance.now();
        const tick = () => {
          if (photo.image && photo.image.width) {
            applyAspect();
          } else if (performance.now() - start < 5000) {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
      }
    }
    sprite.name = dso.name;
    sprite.renderOrder = -998;
    sprite.frustumCulled = false;

    // Lazy-load handle. focusOnDSO calls this on first menu click;
    // we attach the resolved texture, configure its filters,
    // re-derive aspect, and flip usePhoto on. Subsequent calls are
    // no-ops once the texture is loaded, unless a focus asks a phone for
    // a bigger copy than the sky's (photoMaxSize, photoFocusSize).
    if (!photo && photoFiles && photoFiles[dso.name]) {
      const file = photoFiles[dso.name];
      // Long edge in pixels, 0 for the photo as it is; `have` serves `want`.
      const serves = (have, want) => have === 0 || (want !== 0 && have >= want);
      sprite.userData.photoCap = -1;
      sprite.userData.loadPhoto = ({ focus = false } = {}) => {
        const want = (focus && photoFocusSize) || photoMaxSize;
        if (sprite.userData.photoLoaded && serves(sprite.userData.photoCap, want)) return Promise.resolve();
        if (sprite.userData.photoLoading && serves(sprite.userData.photoLoadingCap, want)) return sprite.userData.photoLoading;
        sprite.userData.photoLoadingCap = want;
        sprite.userData.photoLoading = new Promise((resolve) => {
          const url = `${photoBaseUrl}${file}`;
          const fromImage = () => loader.load(
            url,
            (tex) => {
              if (want && !(sprite.userData.photoLoaded && serves(sprite.userData.photoCap, want))) shrinkPhoto(tex, want);
              apply(tex);
            },
            undefined,
            (err) => {
              console.warn(`DSO photo failed: ${dso.name}`, err);
              resolve();
            }
          );
          if (want && typeof createImageBitmap === 'function' && typeof fetch === 'function') {
            loadShrunkPhoto(url, want).then(apply, fromImage);
          } else {
            fromImage();
          }
          function apply(tex) {
            if (sprite.userData.photoLoaded && serves(sprite.userData.photoCap, want)) {
              tex.dispose();
              resolve();
              return;
            }
            const previous = material.uniforms.photoTex.value;
            if (previous && previous !== tex) previous.dispose();
            sprite.userData.photoCap = want;
            tex.colorSpace = THREE.SRGBColorSpace;
            tex.anisotropy = 4;
            tex.wrapS = THREE.ClampToEdgeWrapping;
            tex.wrapT = THREE.ClampToEdgeWrapping;
            tex.minFilter = THREE.LinearMipmapLinearFilter;
            tex.magFilter = THREE.LinearFilter;
            tex.generateMipmaps = true;
            material.uniforms.photoTex.value = tex;
            material.uniforms.usePhoto.value = 1.0;
            const img = tex.image;
            if (img && img.width) {
              const aspect = img.width / img.height;
              const heightDeg = dso.widthDeg / aspect;
              sprite.scale.set(angleToSize(dso.widthDeg), angleToSize(heightDeg), 1);
              material.uniforms.photoAspect.value = aspect;
            }
            sprite.userData.photoLoaded = true;
            resolve();
          }
        });
        return sprite.userData.photoLoading;
      };
    }

    group.add(sprite);
  }

  return {
    group,
    dispose: () => {
      group.children.forEach((sprite) => {
        if (sprite.material) sprite.material.dispose();
      });
    }
  };
}

export { DSO_CATALOG };
